// Employee-facing privacy summary — Employee Portal & App spec §14.
//
// Plain-English "what HR and managers can and cannot see", rendered from the
// shared `privacy-summary` module (the single reviewable source). Describes the
// platform's actual behaviour; the manager "cannot see your Maeve conversations"
// row is the same boundary the §11.2 matrix enforces.

import {
  PRIVACY_SUMMARY_PARAGRAPHS,
  HR_VISIBILITY,
  MANAGER_VISIBILITY,
  type PrivacyVisibilityRow,
} from '@besideu/shared/privacy-summary';
import { Card } from '@/components/ui/Card.tsx';

export function PrivacySummary() {
  return (
    <Card as="section">
      <h2 className="text-lg font-semibold">Privacy</h2>
      <div className="mt-2 space-y-2">
        {PRIVACY_SUMMARY_PARAGRAPHS.map((p) => (
          <p key={p} className="text-sm text-muted-foreground">
            {p}
          </p>
        ))}
      </div>

      <VisibilityList title="What your manager can see" rows={MANAGER_VISIBILITY} />
      <VisibilityList title="What HR can see" rows={HR_VISIBILITY} />
    </Card>
  );
}

function VisibilityList({ title, rows }: { title: string; rows: readonly PrivacyVisibilityRow[] }) {
  return (
    <div className="mt-6">
      <h3 className="text-sm font-medium text-muted-foreground">{title}</h3>
      <ul className="mt-2 space-y-2">
        {rows.map((r) => (
          <li key={r.data} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
            <AccessMark access={r.access} />
            <span className="font-medium">{r.data}</span>
            {r.note && <span className="text-muted-foreground">— {r.note}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function AccessMark({ access }: { access: PrivacyVisibilityRow['access'] }) {
  // Neutral, non-alarming markers — calm tone per §9.2 / §1.4 #8.
  const label =
    access === 'visible' ? 'Can see' : access === 'not_visible' ? 'Cannot see' : 'Only if needed';
  const tone =
    access === 'not_visible'
      ? 'text-traffic-green-text'
      : access === 'conditional'
        ? 'text-traffic-amber-text'
        : 'text-muted-foreground';
  return <span className={`w-24 shrink-0 text-xs font-semibold ${tone}`}>{label}</span>;
}
